/* Kind Gifts Co. — dynamic sticky offset for the collection filter bar.
 *
 * Why this exists: the stuck header stack above #main-collection-filters
 * is not a fixed height. It always includes the announcement strip and the
 * sticky header, but it can also include the desktop nav overlay (only when
 * the header is in its revealed state) and app-injected bars such as the
 * Hoppy free-shipping bar, which appear or disappear independently of the
 * theme. A fixed `top` in rem clips the filter bar underneath the header
 * whenever the real stack renders taller than the assumed value (and leaves
 * a dead gap when it renders shorter), so the offset is measured from the
 * live DOM instead.
 *
 * The CSS `top` values on #main-collection-filters (direction-a-luxe.css)
 * are kept as the no-JS fallback; this script overrides them inline
 * whenever it can measure the stack.
 */
(function () {
  'use strict';

  var bar = document.getElementById('main-collection-filters');
  if (!bar) return;

  /* Elements known to participate in the stuck stack, in any state. */
  var KNOWN_SELECTORS = [
    '.section-announcement-bar',
    '#shopify-section-announcement-bar',
    '.section-header',
    'sticky-header',
    '.header__inline-menu'
  ];

  var candidates = [];
  var lastTop = null;
  var lastStuck = false;

  function visible(el) {
    var cs = window.getComputedStyle(el);
    return (
      cs.display !== 'none' &&
      cs.visibility !== 'hidden' &&
      parseFloat(cs.opacity || '1') !== 0
    );
  }

  function track(el) {
    if (!el || el === bar || bar.contains(el)) return;
    if (candidates.indexOf(el) === -1) candidates.push(el);
  }

  /* Build the candidate list. `full` also scans for app-injected
     sticky/fixed bars (free-shipping bars, promo strips); the full scan
     is the expensive part, so it only runs at init, on load, and on the
     late-injection timers — not on every resize. */
  function collect(full) {
    candidates = [];
    KNOWN_SELECTORS.forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), track);
    });
    if (!full) return;
    Array.prototype.forEach.call(document.body.querySelectorAll('*'), function (el) {
      if (el === bar || bar.contains(el)) return;
      var cs = window.getComputedStyle(el);
      var pos = cs.position;
      if (pos !== 'sticky' && pos !== 'fixed') return;
      if (!visible(el)) return;
      var r = el.getBoundingClientRect();
      if (r.top <= 250 && r.bottom > 2 && r.height > 0 && r.height <= 400) track(el);
    });
  }

  /* Bottom edge of the currently stuck stack, in px from viewport top.
     The stack is built as a CHAIN from the viewport top: an element
     belongs to the stack when its top edge touches the stack built so
     far. A naive "top must be ~0" test fails on this theme — the
     announcement strip sticks at top:0, but the header sticks BELOW it
     at top:3.8rem (var(--kg-sticky-promo-height)), so the header never
     reads as "at the top" and a flat test silently drops its ~69px,
     leaving the filter bar tucked under the header with only a sliver
     showing (Miro, Oct 12). Chaining counts every layer that hangs off
     the layer above it: announcement -> header -> nav overlay. */
  function stuckBottom() {
    var rects = [];
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (!document.contains(el) || !visible(el)) continue;
      var r = el.getBoundingClientRect();
      if (r.height <= 0 || r.bottom <= 0) continue;
      var pos = window.getComputedStyle(el).position;
      var pinned = pos === 'sticky' || pos === 'fixed';
      /* Desktop nav overlay: absolutely positioned inside the sticky
         header, painting directly below it while the header is revealed. */
      var navOverlay =
        el.classList.contains('header__inline-menu') &&
        el.closest('.section-header.shopify-section-header-sticky');
      if (pinned || navOverlay) rects.push(r);
    }
    var bottom = 0;
    var grew = true;
    var guard = 0;
    while (grew && guard++ < 12) {
      grew = false;
      for (var j = 0; j < rects.length; j++) {
        var rect = rects[j];
        if (rect.top <= bottom + 2 && rect.bottom > bottom) {
          bottom = Math.ceil(rect.bottom);
          grew = true;
        }
      }
    }
    return bottom;
  }

  function sync() {
    var h = stuckBottom();
    if (h !== lastTop) {
      lastTop = h;
      /* h === 0 means the whole header stack is hidden (scroll-down
         state): pin the bar to the viewport top instead of leaving
         the stale header-height offset in place — that stale offset
         was the ~130px dead gap above the bar (Miro, Oct 13). */
      bar.style.top = h + 'px';
    }
    /* While the bar is physically stuck, flag it so the CSS can
       stretch its background band upward and seal the see-through
       sliver between the header's visual bottom edge and the bar's
       measured top (Miro, Oct 13). */
    var stuck = bar.getBoundingClientRect().top <= h + 1;
    if (stuck !== lastStuck) {
      lastStuck = stuck;
      bar.classList.toggle('kg-bar-stuck', stuck);
    }
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      sync();
    });
  }

  collect(true);
  sync();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    collect(false);
    lastTop = null;
    sync();
  });
  window.addEventListener('load', function () {
    collect(true);
    lastTop = null;
    sync();
  });

  /* Re-measure exactly when the header flips between its hide/reveal
     states (header.js toggles classes on .section-header). */
  var header = document.querySelector('.section-header');
  if (header && window.MutationObserver) {
    new MutationObserver(function () {
      lastTop = null;
      sync();
    }).observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
  }

  /* App-injected bars (shipping progress, promos) often mount late. */
  setTimeout(function () {
    collect(true);
    lastTop = null;
    sync();
  }, 1500);
  setTimeout(function () {
    collect(true);
    lastTop = null;
    sync();
  }, 4000);
})();
/* Kind Gifts Co. — single-click switching between collection filter panels.
 *
 * Root cause: an open filter panel's summary renders a ::before overlay that
 * covers the entire viewport (position:fixed; inset:0) to catch outside
 * clicks. A click aimed at ANOTHER filter's trigger therefore lands on the
 * open panel's invisible overlay, which merely closes the open panel — the
 * intended panel never opens, so the shopper must click a second time.
 *
 * This handler detects clicks that landed on the overlay (target is an open
 * panel's summary, but the coordinates are outside the summary's own box),
 * closes the open panel, and opens the panel the shopper actually aimed at —
 * resolved via elementFromPoint with the overlays temporarily transparent
 * to hit-testing. Genuine clicks on a summary's own box, and clicks inside
 * an open panel (checkboxes, price inputs), are left to native behavior.
 *
 * Delegated on document (capture phase) so it survives the facets AJAX
 * re-render, which replaces .js-filter innerHTML.
 */
(function () {
  'use strict';

  var bar = document.getElementById('main-collection-filters');
  if (!bar) return;

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;

      var openPanels = bar.querySelectorAll('.facets__disclosure[open]');
      if (!openPanels.length) return;

      var summary = e.target.closest('.facets__disclosure > summary');
      if (!summary || !bar.contains(summary)) return;

      var details = summary.closest('.facets__disclosure');
      var rect = summary.getBoundingClientRect();
      var onOverlay =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;

      if (!onOverlay) {
        // Genuine click on a summary's own box: close any OTHER open panels
        // so only one is open at a time, then let the native toggle run.
        openPanels.forEach(function (d) {
          if (d !== details) d.removeAttribute('open');
        });
        return;
      }

      // The click hit the ::before overlay outside the summary box.
      // Stop the native toggle (which would only close the open panel).
      e.preventDefault();
      e.stopPropagation();

      // See through the overlays to find what the shopper aimed at.
      var summaries = Array.prototype.map.call(openPanels, function (d) {
        return d.querySelector(':scope > summary');
      });
      var prev = summaries.map(function (s) {
        return s.style.pointerEvents;
      });
      summaries.forEach(function (s) {
        s.style.pointerEvents = 'none';
      });
      var under = document.elementFromPoint(e.clientX, e.clientY);
      summaries.forEach(function (s, i) {
        s.style.pointerEvents = prev[i];
      });

      var targetSummary =
        under && under.closest ? under.closest('.facets__disclosure > summary') : null;

      openPanels.forEach(function (d) {
        d.removeAttribute('open');
      });

      if (targetSummary && bar.contains(targetSummary)) {
        targetSummary.closest('.facets__disclosure').setAttribute('open', '');
      }
      // Otherwise the click was truly outside: panels stay closed.
    },
    true
  );
})();
